'use client'

import { useFormatter, useTranslations } from 'next-intl'
import { useEffect } from 'react'

import EmptyIllustration from '@/assets/icons/tasks-feed/empty-illustration.svg'
import type { TaskListTypeFilter } from '@/features/tasks/api/watchTaskApi'
import {
  countMyTasks,
  useMyActionRequiredCount,
  useMyTasksFeed
} from '@/features/tasks/hooks/useMyTasksFeed'
import { getTaskCardDetailPath } from '@/features/tasks/taskCardCta'
import type { TaskGroupStatusResult } from '@/features/tasks/utils/taskGroupStatus'
import { markTaskListNavigation } from '@/lib/productAnalytics'
import { TASKS_MY_STATUSES, type TasksMyStatus } from '@/lib/tasks/tasksTabs'

import type {
  TaskFeedPlatform,
  TaskFeedStatus
} from '../TaskFeedCard/taskFeedCard.constants'
import { TaskFeedRowCard } from '../TaskFeedCard/TaskFeedRowCard'
import { trackTaskFeedCardClick } from '../TaskFeedCard/trackTaskFeedCardClick'
import TasksEmptyState from '../TasksEmptyState'
import TasksFeedGrid, {
  TasksFeedGridError,
  TasksFeedGridLoading
} from '../TasksFeedGrid'
import TasksFeedNav from '../TasksFeedNav'
import { resolveFeedPlatforms } from '../TasksFeedNav/feedPlatforms'
import {
  MY_FEED_SORTS,
  MY_FEED_TYPE_OPTIONS,
  type TasksFeedChip
} from '../TasksFeedNav/tasksFeedNav.constants'
import {
  PLATFORMS_FILTER_ID,
  TYPES_FILTER_ID,
  usePlatformsFilter,
  useTypesFilter
} from '../TasksFeedNav/useFeedFilters'
import type { TasksMyFeedControls } from '../TasksListingSection'

export type TasksMyFeedProps = {
  controls: TasksMyFeedControls
}

const STATUS_LABEL_KEY = {
  all: 'statusAll',
  actionRequired: 'statusActionRequired',
  frozen: 'statusFrozen',
  inProgress: 'statusInProgress',
  ended: 'statusEnded'
} as const satisfies Record<TasksMyStatus, string>

export default function TasksMyFeed({ controls }: TasksMyFeedProps) {
  const { chip: status, sort, types, onPlatformsChange } = controls
  const t = useTranslations('tasksPage.feedNav')
  const tActive = useTranslations('tasksPage.activeSection')
  const format = useFormatter()
  const tClosed = useTranslations('tasksPage.closedSection')
  const tFeed = useTranslations('tasksPage.feed')
  const {
    items,
    platforms: offered,
    platformsFetched,
    isLoading,
    isError,
    hasMore,
    isLoadingMore,
    loadMore,
    refetch
  } = useMyTasksFeed(
    { status, sort, platforms: controls.platforms, types },
    true
  )
  const actionRequiredCount = useMyActionRequiredCount(true)

  // The API filters and orders the lists; the picked platforms only need
  // checking against the Platforms menu once the lists answered.
  const platforms = platformsFetched
    ? resolveFeedPlatforms(controls.platforms, offered)
    : controls.platforms
  // A picked platform left the menu: drop it.
  const platformGone = platforms !== controls.platforms
  useEffect(() => {
    if (platformGone) {
      onPlatformsChange(platforms)
    }
  }, [onPlatformsChange, platformGone, platforms])

  const typesFilter = useTypesFilter(
    MY_FEED_TYPE_OPTIONS,
    types,
    controls.onTypesChange
  )
  const platformsFilter = usePlatformsFilter(
    offered,
    platforms,
    onPlatformsChange
  )
  const narrowed = types.length > 0 || platforms.length > 0

  const chips: TasksFeedChip<TasksMyStatus>[] = TASKS_MY_STATUSES.map(id => ({
    id,
    label: t(STATUS_LABEL_KEY[id]),
    count: id === 'actionRequired' ? actionRequiredCount : undefined
  }))

  const groupBadge = ({
    status,
    retryAt
  }: TaskGroupStatusResult): TaskFeedStatus => {
    switch (status) {
      case 'actionRequired': {
        return { tone: 'error', label: tActive('groupStatusActionRequired') }
      }
      case 'inProgress': {
        return { tone: 'warning', label: tActive('groupStatusInProgress') }
      }
      case 'frozen': {
        return {
          tone: 'info',
          label: retryAt
            ? tActive('groupStatusFrozenUntil', {
                time: formatRetryAt(retryAt, format)
              })
            : tActive('groupStatusFrozen')
        }
      }
      default: {
        return { tone: 'neutral', label: tActive('groupStatusEnded') }
      }
    }
  }

  const navBar = (
    <TasksFeedNav
      view="my"
      onViewChange={controls.onViewChange}
      chips={chips}
      selectedChip={status}
      onChipChange={controls.onChipChange}
      chipsAriaLabel={t('statusAriaLabel')}
      filters={[typesFilter, platformsFilter]}
      sort={sort}
      sortOptions={MY_FEED_SORTS}
      onSortChange={controls.onSortChange}
      onClearAll={
        narrowed || status !== 'all' ? controls.onClearAll : undefined
      }
      countPending={pending => {
        const pickedTypes = (pending[TYPES_FILTER_ID] ??
          []) as TaskListTypeFilter[]
        const pickedPlatforms = (pending[PLATFORMS_FILTER_ID] ??
          []) as TaskFeedPlatform[]
        return {
          queryKey: [
            'tasks',
            'progress',
            'count',
            status,
            pickedTypes,
            pickedPlatforms
          ],
          queryFn: () => countMyTasks(status, pickedTypes, pickedPlatforms)
        }
      }}
    />
  )

  if (isLoading || platformGone) {
    return (
      <>
        {navBar}
        <TasksFeedGridLoading />
      </>
    )
  }

  if (isError) {
    return (
      <>
        {navBar}
        <TasksFeedGridError
          onRetry={() => {
            void refetch()
          }}
        />
      </>
    )
  }

  const emptyText = narrowed
    ? {
        title: tFeed('emptyFilteredTitle'),
        description: tFeed('emptyFilteredDescription')
      }
    : status === 'actionRequired'
    ? {
        title: tActive('emptyActionRequiredTitle'),
        description: tActive('emptyActionRequiredDescription')
      }
    : status === 'frozen'
    ? {
        title: tActive('emptyFrozenTitle'),
        description: tActive('emptyFrozenDescription')
      }
    : status === 'inProgress'
    ? {
        title: tActive('emptyInProgressTitle'),
        description: tActive('emptyInProgressDescription')
      }
    : status === 'ended'
    ? {
        title: tClosed('emptyListTitle'),
        description: tClosed('emptyListDescription')
      }
    : {
        title: tActive('emptyListTitle'),
        description: tActive('emptyListDescription')
      }
  const empty =
    items.length > 0 ? undefined : (
      <TasksEmptyState icon={<EmptyIllustration />} {...emptyText} />
    )

  return (
    <>
      {navBar}
      <TasksFeedGrid
        empty={empty}
        hasMore={hasMore}
        isLoadingMore={isLoadingMore}
        onLoadMore={loadMore}
      >
        {items.map(({ key, row, groupStatus, list }) => {
          const href = getTaskCardDetailPath(
            row,
            list === 'closed' ? { view: 'closed' } : undefined
          )
          if (!href) {
            return null
          }
          return (
            <TaskFeedRowCard
              key={key}
              row={row}
              href={href}
              status={groupStatus ? groupBadge(groupStatus) : undefined}
              showStats={list === 'active'}
              onClick={() => {
                if (list === 'active') {
                  trackTaskFeedCardClick(row)
                } else if (row.cta !== 'action') {
                  markTaskListNavigation()
                }
              }}
            />
          )
        })}
      </TasksFeedGrid>
    </>
  )
}

/** «14:20» today, «8 Oct, 14:20» on another day. */
function formatRetryAt(
  iso: string,
  format: ReturnType<typeof useFormatter>
): string {
  const at = new Date(iso)
  const sameDay = at.toDateString() === new Date().toDateString()
  return format.dateTime(
    at,
    sameDay
      ? { hour: '2-digit', minute: '2-digit' }
      : { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }
  )
}
